import {
	type SmsSettingsDTO,
	type SmsSettingsInput,
	type SmsTestInput,
	type SmsTestResultDTO,
	TenantConfigPlane,
} from '@today/types'
import { apiClient } from './api.client'

/**
 * The SMS route tree a console plane addresses (TO-383 Contract; TO-632 unified
 * settings tree): the tenant plane is slug-keyed `/t/{slug}/admin/settings/sms`,
 * the platform plane is `/admin/tenants/{tenantId}/settings/sms`.
 */
const smsOf = (tenantId: string, tenantSlug: string, plane: TenantConfigPlane) =>
	plane === TenantConfigPlane.PLATFORM
		? `/admin/tenants/${encodeURIComponent(tenantId)}/settings/sms`
		: `/t/${encodeURIComponent(tenantSlug)}/admin/settings/sms`

/** A tenant's SMS settings and test SMS (TO-383). */
export const smsSettingsService = {
	/** `GET …/settings/sms` — never carries a credential. */
	async get(tenantId: string, tenantSlug: string, plane: TenantConfigPlane): Promise<SmsSettingsDTO> {
		const { data } = await apiClient.get<SmsSettingsDTO>(smsOf(tenantId, tenantSlug, plane))
		return data
	},

	/** `PUT …/settings/sms` — replaces the whole settings object. */
	async save(
		tenantId: string,
		tenantSlug: string,
		plane: TenantConfigPlane,
		body: SmsSettingsInput,
	): Promise<SmsSettingsDTO> {
		const { data } = await apiClient.put<SmsSettingsDTO>(smsOf(tenantId, tenantSlug, plane), body)
		return data
	},

	/** `POST …/settings/sms/test` — sends one test SMS. */
	async test(
		tenantId: string,
		tenantSlug: string,
		plane: TenantConfigPlane,
		body: SmsTestInput,
	): Promise<SmsTestResultDTO> {
		const { data } = await apiClient.post<SmsTestResultDTO>(`${smsOf(tenantId, tenantSlug, plane)}/test`, body)
		return data
	},
}
